var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __decorateClass = (decorators, target, key, kind) => {
  var result = kind > 1 ? void 0 : kind ? __getOwnPropDesc(target, key) : target;
  for (var i = decorators.length - 1, decorator; i >= 0; i--)
    if (decorator = decorators[i])
      result = (kind ? decorator(target, key, result) : decorator(result)) || result;
  if (kind && result)
    __defProp(target, key, result);
  return result;
};

// client/language-picker/x-country-picker.ts
import { BehaviorController } from "@adornis/chemistry/controllers/behavior-controller";
import { ObservableController } from "@adornis/chemistry/controllers/observable-controller";
import { SizeController } from "@adornis/chemistry/controllers/size-controller";
import { css } from "@adornis/chemistry/directives/css";
import "@adornis/chemistry/elements/components/x-button";
import "@adornis/chemistry/elements/components/x-card";
import "@adornis/chemistry/elements/components/x-flex";
import { xComponents } from "@adornis/chemistry/elements/x-components";
import { FormField } from "@adornis/forms/form-field";
import "@adornis/forms/x-input";
import "@adornis/popover/a-drop-wrapper";
import { html, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { state } from "lit/decorators/state.js";
import { ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import { combineLatest } from "rxjs";
import { debounceTime, filter, switchMap } from "rxjs/operators";
import { Languages } from "../../languages";
import { getAllCountries, getCountryByCode, getFlagByCode } from "./flag-helper";
var ElementsCountryPicker = class extends FormField {
  constructor() {
    super(...arguments);
    this.language = new BehaviorController(this, Languages.ENGLISH);
    this["empty-state"] = "Enter the name of your country";
    this["max-results"] = 8;
    this.placeholder = "Country";
    this.clearinput = false;
    this.showflag = new BehaviorController(this, false);
    this.flagsize = 30;
    this.input = new BehaviorController(this, void 0);
    this.size = new SizeController(this);
    this.allCountries = new ObservableController(
      this,
      this.language.pipe(
        filter(Boolean),
        switchMap((lang) => getAllCountries(lang))
      )
    );
    this.results = new ObservableController(
      this,
      combineLatest([
        this.input.pipe(filter(Boolean), debounceTime(200)),
        this.allCountries.observable.pipe(filter(Boolean)),
        this.showflag
      ]).pipe(
        switchMap(([input, allCountries, showFlag]) => {
          return Promise.all(
            Object.entries(allCountries).filter(([key, val]) => {
              return val.toUpperCase().startsWith(input.toUpperCase());
            }).map(async ([key, val]) => {
              const flag = showFlag ? await getFlagByCode(key) : void 0;
              return [key, val, flag];
            }).filter((x, i) => i < this["max-results"])
          );
        })
      )
    );
  }
  render() {
    return xComponents(html`
      <a-dropdown ${ref((dropdown) => this._trigger = dropdown)}>
        <x-input
          slot="trigger"
          placeholder=${this.placeholder}
          ${ref((input) => this._input = input)}
          .error=${this.error}
          ${css({ width: "100%" })}
          .value=${this.clearinput ? "" : getCountryByCode(this.value.value, this.language.value)}
          @click=${(e) => {
      this._trigger?.open();
    }}
          @focus=${() => {
      this.input.next("");
      this._trigger?.open();
    }}
          @value-changed=${(e) => {
      const target = e.currentTarget;
      const query = String(target.value.getValue() || "");
      this.input.next(query);
    }}
          @value-picked=${(e) => {
      if (this.clearinput) {
        this.input.next("");
        this._input?.value.next("");
      }
    }}
          @blur=${(e) => {
      if (e.currentTarget.value.value === "") {
        this.value.next(void 0);
        this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: void 0 } }));
      }
    }}
        ></x-input>

        <x-card slot="dropdown-content" ${css({ width: `${this.size.width}px` })}>
          <x-flex> ${this._flagList()} </x-flex>
        </x-card>
      </a-dropdown>
    `);
  }
  _flagList() {
    return html`
      <x-flex space="sm">
        ${this.results.value?.length ? repeat(
      this.results.value || [],
      ([short, country, flag]) => this._singleFlag(short, country, flag, () => {
        this._trigger?.close();
        this.value.next(short);
        this.input.next(getCountryByCode(this.value.value, this.language.value));
        this.dispatchEvent(new CustomEvent("value-picked", { detail: { value: short } }));
      })
    ) : html`<x-text tone="subtle"> ${this["empty-state"]} </x-text>`}
      </x-flex>
    `;
  }
  _singleFlag(lang, country, flag, close) {
    return html`
      <x-flex
        horizontal
        crossaxis-center
        space="sm"
        ${css({ cursor: "pointer", userSelect: "none" })}
        @click=${() => {
      if (close)
        close();
    }}
      >
        ${flag ? html` <img ${css({ width: this.flagsize + "px", height: this.flagsize / 1.5 + "px" })} src="${flag}" />` : nothing}

        <x-text> ${country} </x-text>
      </x-flex>
    `;
  }
};
__decorateClass([
  property()
], ElementsCountryPicker.prototype, "language", 2);
__decorateClass([
  property()
], ElementsCountryPicker.prototype, "empty-state", 2);
__decorateClass([
  property({ type: Number })
], ElementsCountryPicker.prototype, "max-results", 2);
__decorateClass([
  property()
], ElementsCountryPicker.prototype, "placeholder", 2);
__decorateClass([
  property({ type: Boolean })
], ElementsCountryPicker.prototype, "clearinput", 2);
__decorateClass([
  property({ type: Boolean, reflect: true })
], ElementsCountryPicker.prototype, "showflag", 2);
__decorateClass([
  property({ type: Number })
], ElementsCountryPicker.prototype, "flagsize", 2);
__decorateClass([
  state()
], ElementsCountryPicker.prototype, "input", 2);
__decorateClass([
  state()
], ElementsCountryPicker.prototype, "size", 2);
__decorateClass([
  state()
], ElementsCountryPicker.prototype, "allCountries", 2);
__decorateClass([
  state()
], ElementsCountryPicker.prototype, "results", 2);
ElementsCountryPicker = __decorateClass([
  customElement("x-country-picker")
], ElementsCountryPicker);
export {
  ElementsCountryPicker
};
